import clsx from 'clsx'
import { rem } from 'polished'
import { type ReactNode, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import LineArrowsRightDownIcon from 'LuxKit/Icons/line/LineArrowsRightDownIcon'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import type SvgIcon from 'LuxKit/SvgIcon'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import Caption from 'LuxKit/Typography/Caption'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import getPropertyIcon from 'lib/offer/getPropertyIcon'
import getOfferFeatureIcon from 'lib/offer/getOfferFeatureIcon'
import { getMaxNonInfantGuestCount } from 'lib/offer/occupancyUtils'

import { mapRoomPackage } from './roomPackageUtils'

const AMENITY_DIVIDER = ' · '

const ROOM_SIZE_AMENITY_KEY = 'room-size'

const FlexGroup = styled(Group)`
  flex: 1;
`

const WrappableLabel = styled(StructureLabel)`
  padding-block: ${rem(2)};
`

const AmenitiesAndPoliciesCtaContainer = styled.div`
  &.start-on-new-line {
    flex-basis: 100%;
  }
`

const FlowFacts = styled.div`
  line-height: ${rem(16)};
  margin-block-end: ${rem(8)};
  &:empty { display: none; }
`

const FlowFact = styled(CaptionTextBlock)`
  display: inline-flex;
  vertical-align: top;
  max-width: 100%;
  margin-inline-end: ${rem(8)};
  margin-block-end: ${rem(4)};
`

const FlowAmenitySummary = styled(Caption)`
  /* Unlike overflow:hidden, clip leaves line boxes free to flow around the thumbnail. */
  overflow: clip;
  ${mediaQueryUp.desktop} { max-height: calc(var(--text-line-height) * 2); }
  text-wrap: pretty;
  margin-block-end: ${rem(4)};
`

const InlineDetailsCta = styled.span`
  ${mediaQueryUp.desktop} { display: none; }
`

const BlockDetailsCta = styled.div`
  &.below-amenities {
    display: none;
    ${mediaQueryUp.desktop} { display: block; }
  }
`

interface Amenity {
  key: string
  name: string
  Icon: typeof SvgIcon
}

interface RoomPackageDetails {
  size?: string
  capacity?: number
  beds?: string
  bedrooms?: number
  wifi?: string
  parking?: string
  amenities?: Array<Amenity>
}

function extractPropertyRoomPackageDetailList({ capacities, maxOccupancy, bedrooms, bedGroupDescriptions, size, popularAmenities }: App.Property.Room, orWord: string): RoomPackageDetails {
  const totalCapacity = maxOccupancy ?? getMaxNonInfantGuestCount(capacities)

  const amenities: Array<Amenity> = []
  if (size) {
    amenities.push({ key: ROOM_SIZE_AMENITY_KEY, name: size, Icon: LineArrowsRightDownIcon })
  }
  for (const { id, name, iconName } of popularAmenities) {
    amenities.push({ key: id, name, Icon: getPropertyIcon(iconName) })
  }

  return {
    capacity: totalCapacity || undefined,
    beds: bedGroupDescriptions.join(` ${orWord} `) || undefined,
    bedrooms,
    amenities,
  }
}

function extractHotelRoomPackageDetailList(
  roomPackage: App.HotelPackage,
  orWord: string,
  showAmenities: boolean,
): RoomPackageDetails {
  const dimension = roomPackage.roomType.sizeSqm
  const beds = (roomPackage.roomType.bedGroups ?? []).map((b) => b.description).join(` ${orWord} `)
  const bedrooms = roomPackage.roomType.numberOfBedrooms
  const amenities: Array<Amenity> = []

  if (showAmenities && dimension) {
    amenities.push({ key: `${roomPackage.roomType.id}-size`, name: `${dimension}m²`, Icon: LineArrowsRightDownIcon })
  }

  if (showAmenities) {
    for (const amenity of roomPackage.roomType.amenities) {
      amenities.push({
        key: `${amenity.group}-${amenity.id}`,
        name: amenity.description,
        Icon: getOfferFeatureIcon(amenity.symbol),
      })
    }
  }

  return {
    size: showAmenities ? undefined : dimension ? `${dimension}m²` : undefined,
    beds: beds || undefined,
    bedrooms,
    amenities: amenities.length ? amenities : undefined,
  }
}

function extractBedbankRoomPackageDetailList(roomPackage: App.BedbankPackage): RoomPackageDetails {
  const features = new Map(roomPackage.features.map((f) => ([f.id, f.text])))

  return {
    beds: features.get('bed'),
    bedrooms: roomPackage.numberOfBedrooms,
    capacity: getMaxNonInfantGuestCount(roomPackage.capacities.combinations),
    size: features.get('dimension'),
    wifi: features.get('wifi'),
    parking: features.get('parking'),
  }
}

interface Props {
  className?: string
  roomPackage: App.Property.Room | App.HotelPackage | App.BedbankPackage
  urgencyLabels?: ReactNode
  hideCounts?: boolean
  showHotelRoomAmenities?: boolean
  amenitiesAndPoliciesCta?: ReactNode
  showStandaloneAmenitiesAndPoliciesCta?: boolean
  showAmenitiesAndPoliciesCtaOnNewLine?: boolean
  /** Limits amenity entries in the compact overview, including room size. */
  maxCompactAmenities?: number
  /** Compact room overviews use unboxed occupancy facts and inline amenity names. */
  presentation?: 'standard' | 'compact'
}

function AccommodationRoomDetailsListContent(props: Props) {
  const {
    className,
    roomPackage,
    urgencyLabels,
    hideCounts,
    showHotelRoomAmenities = false,
    amenitiesAndPoliciesCta,
    showStandaloneAmenitiesAndPoliciesCta = false,
    showAmenitiesAndPoliciesCtaOnNewLine = false,
    presentation = 'standard',
    maxCompactAmenities,
  } = props

  const intl = useIntl()

  const orWord = intl.formatMessage({
    id: 'GzXJi62SQuqpWHf7Jmwj',
    defaultMessage: 'OR',
    description: 'Separator between alternative bed group options (e.g. "1 King Bed OR 2 Single Beds")',
  })

  const detailsList = useMemo(() => {
    return mapRoomPackage(
      roomPackage,
      (room) => extractPropertyRoomPackageDetailList(room, orWord),
      (pkg) => extractHotelRoomPackageDetailList(pkg, orWord, showHotelRoomAmenities),
      extractBedbankRoomPackageDetailList,
    )
  }, [roomPackage, orWord, showHotelRoomAmenities])

  const bedroomsLabel = (bedroomCount: number) => {
    return intl.formatMessage(
      {
        id: 'TymFMOQjKzT063HM0cSP',
        defaultMessage: '{count, plural, one {# bedroom} other {# bedrooms}}',
        description: 'Label showing the number of bedrooms for a room/package.',
      },
      { count: bedroomCount },
    )
  }

  const sleepsLabel = (guestCount: number) => {
    return intl.formatMessage(
      {
        id: 'rHuohmpw67VoLBruN_2o',
        defaultMessage: 'Sleeps {count}',
        description: 'Label indicating the maximum guest capacity for a room/package.',
      },
      { count: guestCount },
    )
  }

  if (presentation === 'compact') {
    return <div className={className}>
      {urgencyLabels}
      <FlowFacts>
        {detailsList.size && <FlowFact variant="medium" startIcon={<LineArrowsRightDownIcon aria-hidden="true" />}>
          {detailsList.size}
        </FlowFact>}
        {!hideCounts && !!detailsList.capacity && <FlowFact variant="medium" startIcon={<LineUsersAltIcon aria-hidden="true" />}>
          {sleepsLabel(detailsList.capacity)}
        </FlowFact>}
        {detailsList.beds && <FlowFact variant="medium" startIcon={<LineBedIcon aria-hidden="true" />}>
          {detailsList.beds}
        </FlowFact>}
        {!hideCounts && !!detailsList.bedrooms && <FlowFact variant="medium" startIcon={<LineBedIcon aria-hidden="true" />}>
          {bedroomsLabel(detailsList.bedrooms)}
        </FlowFact>}
      </FlowFacts>
      {!!detailsList.amenities?.length && <FlowAmenitySummary variant="medium">
        {detailsList.amenities.slice(0, maxCompactAmenities).map((amenity, index) => <span key={amenity.key}>
          {index > 0 && AMENITY_DIVIDER}{amenity.name}
        </span>)}
        {amenitiesAndPoliciesCta && <InlineDetailsCta>{AMENITY_DIVIDER}{amenitiesAndPoliciesCta}</InlineDetailsCta>}
      </FlowAmenitySummary>}
      {(!!detailsList.amenities?.length || showStandaloneAmenitiesAndPoliciesCta) && amenitiesAndPoliciesCta && <BlockDetailsCta className={clsx({ 'below-amenities': !!detailsList.amenities?.length })}>
        {amenitiesAndPoliciesCta}
      </BlockDetailsCta>}
    </div>
  }

  return <FlexGroup className={className} direction="vertical" horizontalAlign="start" gap={12} noDisplayWhenEmpty>
    <LabelGroup>
      {urgencyLabels}
      {detailsList.size && <StructureLabel variant="standard" startIcon={<LineArrowsRightDownIcon aria-hidden="true" />}>
        {detailsList.size}
      </StructureLabel>}
      {!hideCounts && detailsList.capacity && <StructureLabel variant="standard" startIcon={<LineUsersAltIcon aria-hidden="true" />}>
        {sleepsLabel(detailsList.capacity)}
      </StructureLabel>}
      {detailsList.beds && <WrappableLabel variant="standard" startIcon={<LineBedIcon aria-hidden="true" />}>
        {detailsList.beds}
      </WrappableLabel>}
      {!hideCounts && !!detailsList.bedrooms && <WrappableLabel variant="standard" startIcon={<LineBedIcon aria-hidden="true" />}>
        {bedroomsLabel(detailsList.bedrooms)}
      </WrappableLabel>}
    </LabelGroup>
    {(!!detailsList.amenities || (showStandaloneAmenitiesAndPoliciesCta && !!amenitiesAndPoliciesCta)) && <Group direction="horizontal" gap="8 16" wrap="wrap">
      {detailsList.amenities?.map(({ key, name, Icon }) => <BodyTextBlock key={key} variant="medium" startIcon={<Icon aria-hidden="true" />}>
        {name}
      </BodyTextBlock>)}
      {amenitiesAndPoliciesCta && <AmenitiesAndPoliciesCtaContainer
        className={clsx({ 'start-on-new-line': showAmenitiesAndPoliciesCtaOnNewLine })}
        data-testid="accommodation-room-details-cta-container"
      >
        {amenitiesAndPoliciesCta}
      </AmenitiesAndPoliciesCtaContainer>}
    </Group>}
  </FlexGroup>
}

export default AccommodationRoomDetailsListContent
